import { cache } from 'react';
import { featureFlipping, Locale } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { CommonDI, LoyaltyConfigsRepositoryType, ProductsRepositoryType } from '../../../../DI';
import { PaymentMethodsValue, ProductDetailValue, VipBrandsValue } from '../../../../server';

/**
 * Fetches product detail information with cache capabilities.
 *
 * @param {Locale} locale - The locale for the request.
 * @param {string} productId - The ID of the product to fetch.
 * @param {VipBrandsValue} vipBrands - VIP brands information.
 * @param {PaymentMethodsValue} paymentMethods - Payment methods information.
 * @returns {Promise<ProductDetailValue | undefined>} The detailed product information or undefined if not found.
 */
export const getProductDetailInfo = cache(
    async (
        locale: Locale,
        productId: string,
        vipBrands: VipBrandsValue,
        paymentMethods: PaymentMethodsValue,
    ): Promise<ProductDetailValue | undefined> => {
        const loyaltyConfigResult = await (featureFlipping.isLoyaltyConfigEnabled
            ? CommonDI.get(LoyaltyConfigsRepositoryType).fetchLoyaltyConfiguration(locale)
            : Promise.resolve(undefined));

        const loyaltyConfig = unwrapResultSuccess(loyaltyConfigResult);

        const productResult = await CommonDI.get(ProductsRepositoryType).getProductById({
            locale,
            id: productId,
            vipBrands,
            paymentMethods,
            needScope: true,
            isPreviewable: false,
            loyaltyConfig,
        });

        if (productResult.type === 'empty' || productResult.type === 'failure') {
            return undefined;
        }

        return productResult.data;
    },
);
